import { useEffect, useMemo, useState } from "react";
import { BsCashStack, BsGiftFill } from "react-icons/bs";
import { LuDownload, LuEye, LuTrash2 } from "react-icons/lu";
import { RiWallet3Line } from "react-icons/ri";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import MultiLineText from "../../../components/MultiLineText";
import NewTableComponent from "../../../components/NewTableComponent";
import Stats from "../../../components/Stats";
import UserAvatar from "../../../components/UserAvatar";
import ConfirmDeleteModal from "../../../components/modals/ConfirmDeleteModal";
import { apiService } from "../../../services/apiService";
import "../../../styles/Access.css";
import "../../../styles/DriverPayments.css";
import "../../../styles/DriverSafety.css";
import "../../../styles/Payroll.css";
import "../../../styles/Services.css";
import "../../../styles/Table.css";
import "../../../styles/TripCostEstimation.css";
import {
  AppMainModules,
  FinancePermission,
  FinanceSubModules,
} from "../../../utils/app-constant";
import { hasPermission } from "../../../utils/helper";
import { plusIcon } from "../../../utils/svgs";
import { errorNotify, successNotify } from "../../../utils/toast";

const PAYROLL_STORAGE_KEY = "fms-payroll-runs-v2";
const defaultPayrollRuns = [];

export const getPayrollRuns = () => {
  try {
    const storedRuns = JSON.parse(localStorage.getItem(PAYROLL_STORAGE_KEY) || "null");
    return Array.isArray(storedRuns) && storedRuns.length ? storedRuns : defaultPayrollRuns;
  } catch (error) {
    return defaultPayrollRuns;
  }
};

export const setPayrollRuns = (runs) => {
  localStorage.setItem(PAYROLL_STORAGE_KEY, JSON.stringify(runs));
};

export const formatPayrollCurrency = (value) =>
  `PKR ${new Intl.NumberFormat("en-PK", { maximumFractionDigits: 0 }).format(Number(value || 0))}`;

export const getPayrollStatusClassName = (status = "") => {
  const value = String(status).toLowerCase();
  if (["locked", "approved"].includes(value)) return "completed";
  if (value === "submitted") return "pending";
  if (value === "rejected") return "late";
  return "draft";
};

const buildStatIcon = (icon) => <span className="finance_metric_icon">{icon}</span>;

const Payroll = () => {
  const navigate = useNavigate();
  const canCreatePayroll = hasPermission({
    permissionSlug: FinancePermission.Create,
    menuSlug: FinanceSubModules.Payroll,
    moduleSlug: AppMainModules.finance,
  });

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [searchFilters, setSearchFilters] = useState({});
  const [paginationMeta, setPaginationMeta] = useState({
    totalItems: 0,
    totalPages: 1,
    currentPage: 1,
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [deletePopup, setDeletePopup] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchPayrollData = async () => {
      setLoading(true);
      try {
        const response = await apiService.getDriverPayrollPaginated({
          page: currentPage,
          limit,
          search_text: searchText,
          searchFilters,
        });

        const pageData = response?.data || response || {};
        const results = Array.isArray(pageData?.results) ? pageData.results : [];
        const nextMeta = pageData?.meta || {};

        if (!isMounted) return;

        setRecords(results);
        setPaginationMeta({
          totalItems: Number(nextMeta?.totalItems ?? pageData?.total ?? results.length),
          totalPages: Math.max(1, Number(nextMeta?.totalPages ?? 1)),
          currentPage: Math.max(1, Number(nextMeta?.currentPage ?? currentPage)),
        });
      } catch (error) {
        if (!isMounted) return;
        setRecords([]);
        setPaginationMeta({
          totalItems: 0,
          totalPages: 1,
          currentPage: 1,
        });
        errorNotify(error || "Failed to fetch payroll data");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPayrollData();

    return () => {
      isMounted = false;
    };
  }, [currentPage, limit, searchText, searchFilters, refreshTrigger]);

  const overview = useMemo(
    () => ({
      totalSalariesGiven: records
        .filter((row) => ["locked", "approved"].includes(String(row.status).toLowerCase()))
        .reduce((sum, row) => sum + Number(row.total_net_salary || row.net_salary || 0), 0),
      totalAllowances: records.reduce((sum, row) => sum + Number(row.total_allowances || row.allowances || 0), 0),
      totalBonuses: records.reduce((sum, row) => sum + Number(row.total_bonuses || row.bonuses || 0), 0),
      totalPayrollRuns: paginationMeta.totalItems,
    }),
    [records, paginationMeta.totalItems]
  );

  const statsData = [
    {
      title: "Total Salaries Given",
      value: formatPayrollCurrency(overview.totalSalariesGiven),
      text: <span>Net pay from locked runs</span>,
      icon: buildStatIcon(<RiWallet3Line />),
    },
    {
      title: "Total Allowances",
      value: formatPayrollCurrency(overview.totalAllowances),
      text: <span>Across all runs</span>,
      className: "completed",
      icon: buildStatIcon(<BsCashStack />),
    },
    {
      title: "Total Bonuses",
      value: formatPayrollCurrency(overview.totalBonuses),
      text: <span>Across all runs</span>,
      className: "late",
      icon: buildStatIcon(<BsGiftFill />),
    },
    {
      title: "Total Payroll Runs",
      value: overview.totalPayrollRuns,
      text: <span>All payroll runs</span>,
      className: "pending-delivery",
      icon: buildStatIcon(<RiWallet3Line />),
    },
  ];

  const handleOpenDetails = (row) => {
    const formattedRecord = {
      id: row.id,
      payrollNo: `PR-${row.payroll_id || row.id}`,
      monthLabel: row.month_name && row.year ? `${row.month_name} ${row.year}` : (row.created_at ? new Date(row.created_at).toLocaleString("en-US", { month: "long", year: "numeric" }) : "N/A"),
      periodLabel: row.start_at && row.end_at 
        ? `${new Date(row.start_at).toLocaleDateString()} - ${new Date(row.end_at).toLocaleDateString()}`
        : (row.created_at ? `Created: ${new Date(row.created_at).toLocaleDateString()}` : "N/A"),
      status: row.status || "draft",
      totalSalary: row.total_basic_salary || row.basic_salary,
      allowances: row.total_allowances || row.allowances || 0,
      bonuses: row.total_bonuses || row.bonuses || 0,
      deductions: Number(row.total_deductions || row.deductions || 0) + Number(row.total_shortage_penalty || row.shortage_penalty || 0),
      netPay: row.total_net_salary || row.net_salary,
      driverItems: (row.payroll_drivers || []).map((pd) => ({
        id: pd.id,
        driverId: `DRV-${pd.driver?.id || pd.driver_id}`,
        driverName: pd.driver?.username || pd.driver?.name || `Driver #${pd.driver_id}`,
        daysWorked: pd.days_worked || pd.working_days || 0,
        dailyRate: pd.days_worked ? Number(pd.basic_salary) / Number(pd.days_worked) : Number(pd.basic_salary),
        basePay: pd.basic_salary,
        allowances: pd.allowances || 0,
        bonuses: pd.bonuses || 0,
        manualDeductions: pd.deductions || 0,
        shortagePenalization: pd.shortage_penalty || 0,
        netPay: pd.net_salary || 0,
      }))
    };
    navigate(`/payroll/${row.id}`, { state: { payrollRun: formattedRecord } });
  };

  const handleDownload = (row) => {
    const lines = [
      ["Payroll #", `PR-${row.payroll_id || row.id}`],
      ["Month", row.month_name || `${row.month}/${row.year}`],
      ["Notes", row.notes || ""],
      ["Total Basic Salary", formatPayrollCurrency(row.total_basic_salary)],
      ["Total Allowances", formatPayrollCurrency(row.total_allowances || 0)],
      ["Total Bonuses", formatPayrollCurrency(row.total_bonuses)],
      ["Total Deductions", formatPayrollCurrency(row.total_deductions)],
      ["Total Shortage Penalty", formatPayrollCurrency(row.total_shortage_penalty)],
      ["Total Gross Salary", formatPayrollCurrency(row.total_gross_salary)],
      ["Total Net Salary", formatPayrollCurrency(row.total_net_salary)],
      ["Status", row.status],
    ].map(([label, value]) => `${label}: ${value}`);
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `driver-payroll-${row.id}.txt`;
    link.click();
    URL.revokeObjectURL(url);
    successNotify("Payroll downloaded successfully");
  };

  const closeDeletePopup = () => {
    setDeletePopup(false);
    setDeleteTargetId(null);
  };

  const handleDeleteClick = (id) => {
    setDeleteTargetId(id);
    setDeletePopup(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;

    try {
      const response = await apiService.deleteDriverPayroll(deleteTargetId);
      successNotify(response?.message || "Payroll run deleted successfully");
      closeDeletePopup();
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      errorNotify(error || "Failed to delete payroll run");
    }
  };

  const columns = [
    {
      name: "Payroll #",
      key: "payroll_no",
      selector: (row) => (
        <button
          type="button"
          className="finance_link_button finance_link_text"
          onClick={() => handleOpenDetails(row)}
        >
          PR-{row.payroll_id || row.id}
        </button>
      ),
      tableColumn: true,
      filterName: "payrollNoFilter",
      type: "text",
      minWidth: "150px",
    },
    {
      name: "Drivers ID",
      key: "driver_id",
      selector: (row) => {
        const driversList = Array.isArray(row.payroll_drivers) ? row.payroll_drivers : [];
        const driverIds = driversList
          .map((pd) => {
            const driverId = pd?.driver?.id || pd?.driver_id || "";
            return driverId ? `DRV-${driverId}` : "";
          })
          .filter(Boolean);

        if (!driverIds.length) {
          if (row.driver_id) {
            driverIds.push(`DRV-${row.driver_id}`);
          }
        }

        if (driverIds.length === 0) {
          return <span>-</span>;
        }

        if (driverIds.length === 1) {
          return (
            <span className="service-task">
              <MultiLineText text={driverIds[0]} maxWidth="150px" />
            </span>
          );
        }

        const remainingCount = driverIds.length - 1;

        return (
          <div className="service-tasks-container">
            <span className="first-task">
              <MultiLineText text={driverIds[0]} maxWidth="150px" />
            </span>

            <OverlayTrigger
              placement="top"
              overlay={(
                <Tooltip>
                  <div>
                    {driverIds.slice(1).map((driverId, index) => (
                      <div key={`${driverId}-${index}`} className="tooltip_item">{driverId}</div>
                    ))}
                  </div>
                </Tooltip>
              )}
            >
              <span className="additional-tasks">+{remainingCount}</span>
            </OverlayTrigger>
          </div>
        );
      },
      tableColumn: true,
      filterName: "driverFilter",
      type: "text",
      minWidth: "200px",
    },
    {
      name: "Driver Name",
      key: "driver_name",
      selector: (row) => {
        const driversList = Array.isArray(row.payroll_drivers) ? row.payroll_drivers : [];
        const names = driversList
          .map((pd) => {
            const raw = pd?.driver?.username || pd?.driver?.name || "";
            return raw
              ? raw.toLowerCase().split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
              : "";
          })
          .filter(Boolean);

        if (names.length === 0) return <span>-</span>;

        if (names.length === 1) {
          return <span>{names[0]}</span>;
        }

        return (
          <div className="service-tasks-container">
            <span>{names[0]}</span>
            <OverlayTrigger
              placement="top"
              overlay={(
                <Tooltip>
                  <div>
                    {names.slice(1).map((name, index) => (
                      <div key={`${name}-${index}`} className="tooltip_item">{name}</div>
                    ))}
                  </div>
                </Tooltip>
              )}
            >
              <span className="additional-tasks">+{names.length - 1}</span>
            </OverlayTrigger>
          </div>
        );
      },
      tableColumn: true,
      filterName: "driverNameFilter",
      type: "text",
      minWidth: "200px",
    },
    {
      name: "Basic Salary",
      key: "basic_salary",
      selector: (row) => formatPayrollCurrency(row.total_basic_salary || row.basic_salary),
      tableColumn: true,
      filterName: "basicSalaryFilter",
      type: "number",
      minWidth: "160px",
    },
    {
      name: "Allowances",
      key: "allowances",
      selector: (row) => formatPayrollCurrency(row.total_allowances || row.allowances),
      tableColumn: true,
      filterName: "allowancesFilter",
      type: "number",
      minWidth: "160px",
    },
    {
      name: "Bonuses",
      key: "bonuses",
      selector: (row) => formatPayrollCurrency(row.total_bonuses || row.bonuses),
      tableColumn: true,
      filterName: "bonusesFilter",
      type: "number",
      minWidth: "150px",
    },
    {
      name: "Deductions",
      key: "deductions",
      selector: (row) => formatPayrollCurrency(row.total_deductions || row.deductions),
      tableColumn: true,
      filterName: "deductionsFilter",
      type: "number",
      minWidth: "160px",
    },
    {
      name: "Shortage Penalty",
      key: "shortage_penalty",
      selector: (row) => formatPayrollCurrency(row.total_shortage_penalty || row.shortage_penalty),
      tableColumn: true,
      filterName: "shortagePenaltyFilter",
      type: "number",
      minWidth: "170px",
    },
    {
      name: "Gross Salary",
      key: "gross_salary",
      selector: (row) => formatPayrollCurrency(row.total_gross_salary || row.gross_salary),
      tableColumn: true,
      filterName: "grossSalaryFilter",
      type: "number",
      minWidth: "160px",
    },
    {
      name: "Net Salary",
      key: "net_salary",
      selector: (row) => formatPayrollCurrency(row.total_net_salary || row.net_salary),
      tableColumn: true,
      filterName: "netSalaryFilter",
      type: "number",
      minWidth: "150px",
    },
    {
      name: "Status",
      key: "status",
      selector: (row) => {
        const rawStatus = row.status || "";
        const formattedStatus = rawStatus
          ? rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase()
          : "--";
        return (
          <span className={`payroll_status_badge is_${getPayrollStatusClassName(rawStatus)}`}>
            {formattedStatus}
          </span>
        );
      },
      tableColumn: true,
      filterName: "statusFilter",
      type: "text",
      minWidth: "140px",
    },
    {
      name: "Created By",
      key: "created_by",
      selector: (row) => {
        const creatorName = row.user?.username || `User #${row.created_by}`;
        const creatorImage = row.user?.image || "";
        return (
          <div className="finance_created_by_cell">
            <UserAvatar
              username={creatorName}
              image={creatorImage}
              marginRight="0px"
            />
            <span className="finance_created_by_name">{creatorName}</span>
          </div>
        );
      },
      tableColumn: true,
      filterName: "createdByFilter",
      type: "text",
      minWidth: "220px",
    },
    {
      name: "Action",
      key: "action",
      selector: (row) => (
        <div className="table_action_btn">
          <button
            type="button"
            className="edit_btn"
            title="View"
            onClick={() => handleOpenDetails(row)}
          >
            <LuEye />
          </button>
          <button
            type="button"
            className="edit_btn"
            style={{ color: "#FA6262" }}
            title="Delete"
            onClick={() => handleDeleteClick(row.id)}
          >
            <LuTrash2 />
          </button>
        </div>
      ),
      tableColumn: false,
      minWidth: "160px",
      grow: 0,
    },
  ];

  return (
    <div className="payroll_page driver_payments_page trip_cost_estimation_page">
      <Breadcrumbs values={["Finance", "Payroll"]} />

      <div className="access_view">
        <div>
          <h2>Payroll</h2>
          <p className="payroll_page_subtitle">
            Run-level approval - driver-level calculation - locked total flows to P&amp;L as
            company-wide salary cost
          </p>
        </div>

        <div className="services_buttons">
          {canCreatePayroll && (
            <button type="button" onClick={() => navigate("/payroll/create")}>
              {plusIcon} Create Payroll Run
            </button>
          )}
        </div>
      </div>

      <Stats statsData={statsData} />

      <NewTableComponent
        showAddBtn={false}
        columns={columns}
        data={records}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        limit={limit}
        setLimit={(nextLimit) => {
          setLimit(nextLimit);
          setCurrentPage(1);
        }}
        totalItems={paginationMeta.totalItems}
        totalPages={paginationMeta.totalPages}
        onSearch={(value) => {
          setSearchText(value);
          setCurrentPage(1);
        }}
        searchValue={searchText}
        loading={loading}
        paginationLoading={loading}
        setSearchFilters={setSearchFilters}
        searchFilters={searchFilters}
        showFilter={false}
        limitOptions={[10, 20, 50, 100]}
        tableClassName="payroll_table parts_table_fix driver_status_table"
      />

      <ConfirmDeleteModal
        show={deletePopup}
        onClose={closeDeletePopup}
        onConfirm={handleDeleteConfirm}
        title="Delete payroll run?"
        description="Deleting this payroll run will permanently remove it from the list."
      />
    </div>
  );
};

export default Payroll;
